import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import HeaderTS from "../global/headerTS";
import Footer2 from "../global/footerv2";
import SectionTitle from "../section-title";
import Ctav3 from "../ctav3";
import tcmData from "../../data/tcm-system.json";

// every screenshot is captured twice; the Chinese UI copy carries a -tc suffix
const localizedImage = (src, lng) =>
  lng && lng.startsWith("tc") ? src.replace(/\.webp$/, "-tc.webp") : src;

const ContactButtons = ({ hero, i18nPrefix }) => (
  <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-sm-start gap-3">
    <a
      rel="noreferrer"
      href={`tel:${hero.contactPhone}`}
      className="btn__primary2 btn-md smooth"
    >
      <span>
        <i className="icofont-phone icofont-1x"></i>
        &nbsp;&nbsp;&nbsp;
        <Trans i18nKey={`${i18nPrefix}.phone`}>Phone</Trans>
      </span>
    </a>
    <a
      rel="noreferrer"
      href={`https://wa.me/${hero.contactWhatsApp.replace(/[\s+]/g, "")}`}
      className="btn__primary2 btn-md smooth"
      target="_blank"
    >
      <span>
        <i className="icofont-brand-whatsapp icofont-1x"></i>
        &nbsp;&nbsp;&nbsp;
        <Trans i18nKey={`${i18nPrefix}.whatsapp`}>WhatsApp Us</Trans>
      </span>
    </a>
    <a
      rel="noreferrer"
      href={`mailto:${hero.contactEmail}`}
      className="btn__primary2 btn-md smooth"
    >
      <span>
        <i className="icofont-email icofont-1x"></i>
        &nbsp;&nbsp;&nbsp;
        <Trans i18nKey={`${i18nPrefix}.email`}>Email</Trans>
      </span>
    </a>
  </div>
);

// tabbed screenshot in a browser-style frame, used by the How It Works sections
const ShotTabs = ({ screens, i18nPrefix, lng }) => {
  const [activeId, setActiveId] = useState(screens[0].id);
  const shot = screens.find((s) => s.id === activeId) || screens[0];
  const src = localizedImage(shot.image, lng);
  return (
    <div className="tcm-shot">
      {screens.length > 1 && (
        <div className="erp-screens__tabs tcm-shot__tabs" role="tablist">
          {screens.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={s.id === shot.id}
              className={`erp-screens__tab ${s.id === shot.id ? "is-active" : ""}`}
              onClick={() => setActiveId(s.id)}
            >
              <i className={s.icon}></i>
              <Trans i18nKey={`${i18nPrefix}.screen${s.id}.title`}>
                {s.title}
              </Trans>
            </button>
          ))}
        </div>
      )}
      <div className="erp-screens__frame tcm-shot__frame" role="tabpanel">
        <div className="erp-screens__frame--bar">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <a href={src} target="_blank" rel="noreferrer">
          <img key={src} className="img-fluid" src={src} alt={shot.title} />
        </a>
      </div>
      <p className="tcm-shot__caption">
        <Trans i18nKey={`${i18nPrefix}.screen${shot.id}.description`}>
          {shot.description}
        </Trans>
      </p>
    </div>
  );
};

// icon + title + text rows, the about2 list from the Reboot template
const ItemList = ({ items, i18nPrefix }) => (
  <div className="about2__items tcm-items">
    {items.map((item) => (
      <div key={item.id} className="about2__items--single tcm-items__single">
        <div className="tcm-items__icon">
          <i className={item.icon}></i>
        </div>
        <div className="about2__items--text">
          <h3>
            <Trans i18nKey={`${i18nPrefix}.item${item.id}.title`}>
              {item.title}
            </Trans>
          </h3>
          <p>
            <Trans i18nKey={`${i18nPrefix}.item${item.id}.description`}>
              {item.description}
            </Trans>
          </p>
        </div>
      </div>
    ))}
  </div>
);

const TcmSystem = ({ header }) => {
  const {
    tcmHero,
    tcmFeatures,
    tcmFlow,
    tcmDiagnosis,
    tcmTreatment,
    tcmInventory,
    tcmScreens,
    tcmDeploy,
    tcmPricing,
    tcmDoctors,
  } = tcmData;
  const { i18n } = useTranslation();
  const lng = i18n.language;
  const isTc = lng && lng.startsWith("tc");

  const [billing, setBilling] = useState("monthly");
  const isYearly = billing === "yearly";
  const [activeScreen, setActiveScreen] = useState(tcmScreens.screens[0].id);
  const screen =
    tcmScreens.screens.find((s) => s.id === activeScreen) ||
    tcmScreens.screens[0];
  const screenSrc = localizedImage(screen.image, lng);
  const table = tcmInventory.table;

  return (
    <>
      <HeaderTS header={header} />

      {/* Hero Section */}
      <section id="hero" className="hero3 hero-padding bg-one tcm-hero">
        <div className="container">
          <div className="row align-items-center gy-5">
            <div className="col-lg-6 hero3__content">
              <p className="badge-text">
                <Trans i18nKey="tcmSystem.hero.subtitle">{tcmHero.subtitle}</Trans>
              </p>
              <h1 className="display-5 mb-4 hero3__title">
                <Trans i18nKey="tcmSystem.hero.title">{tcmHero.title}</Trans>
              </h1>
              <p className="fs-5 mb-4">
                <Trans i18nKey="tcmSystem.hero.description">
                  {tcmHero.description}
                </Trans>
              </p>
              <p className="tcm-hero__price">
                <i className="icofont-tag"></i>
                <Trans i18nKey="tcmSystem.hero.price">{tcmHero.price}</Trans>
              </p>
              <ContactButtons hero={tcmHero} i18nPrefix="tcmSystem.hero" />
            </div>

            <div className="col-lg-6">
              <div className="tcm-hero__stack">
                <div className="tcm-hero__window tcm-hero__window--back">
                  <div className="erp-screens__frame--bar">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <img
                    src={localizedImage(tcmHero.imageBack, lng)}
                    alt={tcmHero.subtitle}
                  />
                </div>
                <div className="tcm-hero__window tcm-hero__window--front">
                  <div className="erp-screens__frame--bar">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <img
                    src={localizedImage(tcmHero.imageFront, lng)}
                    alt={tcmHero.subtitle}
                  />
                </div>
                <ul className="tcm-hero__chips">
                  {tcmHero.chips.map((chip) => (
                    <li key={chip.id}>
                      <i className={chip.icon}></i>
                      <Trans i18nKey={`tcmSystem.hero.chip${chip.id}`}>
                        {chip.title}
                      </Trans>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section id="features" className="service section-padding tcm-features">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="tcmSystem.features.subtitle">
                {tcmFeatures.subtitle}
              </Trans>
            }
            title={
              <Trans i18nKey="tcmSystem.features.title">
                {tcmFeatures.title}
              </Trans>
            }
            direction="center"
          />
          <div className="row pt-4 gy-5">
            {tcmFeatures.features.map((feature) => (
              <div key={feature.id} className="col-md-6 col-xl-3">
                <div className="service__card translateEffect1 h-100">
                  <div className="service__card__icon">
                    <i className={feature.icon}></i>
                  </div>
                  <div className="service__card__content">
                    <h4>
                      <Trans i18nKey={`tcmSystem.features.feature${feature.id}.title`}>
                        {feature.title}
                      </Trans>
                    </h4>
                    <p>
                      <Trans
                        i18nKey={`tcmSystem.features.feature${feature.id}.description`}
                      >
                        {feature.description}
                      </Trans>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Patient Flow Section */}
      <section id="flow" className="section-padding erp-flow bg-one">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="tcmSystem.flow.subtitle">{tcmFlow.subtitle}</Trans>
            }
            title={<Trans i18nKey="tcmSystem.flow.title">{tcmFlow.title}</Trans>}
            direction="center"
          />
          <ol className="erp-flow__ribbon">
            {tcmFlow.steps.map((step, i) => (
              <li
                key={step.id}
                // earlier panels paint over the notch of the next one
                style={{ zIndex: tcmFlow.steps.length - i }}
                className={`erp-flow__ribbon--step${
                  step.highlight ? " is-final" : ""
                }`}
              >
                <i className={step.icon}></i>
                <h5>
                  <Trans i18nKey={`tcmSystem.flow.step${step.id}`}>
                    {step.title}
                  </Trans>
                </h5>
                <span className="erp-flow__ribbon--doc">
                  <Trans i18nKey={`tcmSystem.flow.doc${step.id}`}>
                    {step.doc}
                  </Trans>
                </span>
              </li>
            ))}
          </ol>
          <div className="erp-flow__note">
            <i className="icofont-check-circled"></i>
            <p>
              <Trans i18nKey="tcmSystem.flow.note">{tcmFlow.note}</Trans>
            </p>
          </div>
        </div>
      </section>

      {/* How It Works 1: Four Examinations */}
      <section id="diagnosis" className="about2 section-padding tcm-how">
        <div className="container">
          <div className="row gx-xl-5 gy-5 align-items-center">
            <div className="col-lg-5">
              <SectionTitle
                subtitle={
                  <Trans i18nKey="tcmSystem.diagnosis.subtitle">
                    {tcmDiagnosis.subtitle}
                  </Trans>
                }
                title={
                  <Trans i18nKey="tcmSystem.diagnosis.title">
                    {tcmDiagnosis.title}
                  </Trans>
                }
                description={
                  <Trans i18nKey="tcmSystem.diagnosis.description">
                    {tcmDiagnosis.description}
                  </Trans>
                }
              />
              <ItemList
                items={tcmDiagnosis.items}
                i18nPrefix="tcmSystem.diagnosis"
              />
            </div>
            <div className="col-lg-7">
              <ShotTabs
                screens={tcmDiagnosis.screens}
                i18nPrefix="tcmSystem.diagnosis"
                lng={lng}
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works 2: Internal and External Treatments */}
      <section id="treatment" className="about2 section-padding bg-one tcm-how">
        <div className="container">
          <div className="row gx-xl-5 gy-5 align-items-center">
            <div className="col-lg-7 order-2 order-lg-1">
              <ShotTabs
                screens={tcmTreatment.screens}
                i18nPrefix="tcmSystem.treatment"
                lng={lng}
              />
            </div>
            <div className="col-lg-5 order-1 order-lg-2">
              <SectionTitle
                subtitle={
                  <Trans i18nKey="tcmSystem.treatment.subtitle">
                    {tcmTreatment.subtitle}
                  </Trans>
                }
                title={
                  <Trans i18nKey="tcmSystem.treatment.title">
                    {tcmTreatment.title}
                  </Trans>
                }
                description={
                  <Trans i18nKey="tcmSystem.treatment.description">
                    {tcmTreatment.description}
                  </Trans>
                }
              />
              {tcmTreatment.groups.map((group) => (
                <div key={group.id} className="tcm-group">
                  <h4 className="tcm-group__title">
                    <Trans i18nKey={`tcmSystem.treatment.group${group.id}.title`}>
                      {group.title}
                    </Trans>
                  </h4>
                  <ItemList
                    items={group.items}
                    i18nPrefix={`tcmSystem.treatment.group${group.id}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works 3: Inventory */}
      <section id="inventory" className="about2 section-padding tcm-how tcm-inventory">
        <div className="container">
          <div className="row gx-xl-5 gy-5 align-items-center">
            <div className="col-lg-5">
              <SectionTitle
                subtitle={
                  <Trans i18nKey="tcmSystem.inventory.subtitle">
                    {tcmInventory.subtitle}
                  </Trans>
                }
                title={
                  <Trans i18nKey="tcmSystem.inventory.title">
                    {tcmInventory.title}
                  </Trans>
                }
                description={
                  <Trans i18nKey="tcmSystem.inventory.description">
                    {tcmInventory.description}
                  </Trans>
                }
              />
              <ItemList
                items={tcmInventory.items}
                i18nPrefix="tcmSystem.inventory"
              />
            </div>
            <div className="col-lg-7">
              <ShotTabs
                screens={tcmInventory.screens}
                i18nPrefix="tcmSystem.inventory"
                lng={lng}
              />
            </div>
          </div>

          <ol className="erp-flow__ribbon tcm-chain">
            {tcmInventory.chain.map((step, i) => (
              <li
                key={step.id}
                style={{ zIndex: tcmInventory.chain.length - i }}
                className={`erp-flow__ribbon--step${
                  step.highlight ? " is-final" : ""
                }`}
              >
                <i className={step.icon}></i>
                <h5>
                  <Trans i18nKey={`tcmSystem.inventory.chain${step.id}`}>
                    {step.title}
                  </Trans>
                </h5>
              </li>
            ))}
          </ol>

          <div className="tcm-deduct">
            <h4 className="tcm-deduct__title">
              <i className="icofont-pills"></i>{" "}
              <Trans i18nKey="tcmSystem.inventory.tableTitle">{table.title}</Trans>
            </h4>
            <div className="table-responsive">
              <table className="tcm-deduct__table">
                <thead>
                  <tr>
                    <th>
                      <Trans i18nKey="tcmSystem.inventory.herb">{table.herb}</Trans>
                    </th>
                    <th>
                      <Trans i18nKey="tcmSystem.inventory.before">{table.before}</Trans>
                    </th>
                    <th>
                      <Trans i18nKey="tcmSystem.inventory.after">{table.after}</Trans>
                    </th>
                    <th>
                      <Trans i18nKey="tcmSystem.inventory.deducted">
                        {table.deducted}
                      </Trans>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        {row.zh}
                        {!isTc && <span className="tcm-deduct__en">{row.en}</span>}
                      </td>
                      <td>{row.before}</td>
                      <td>{row.after}</td>
                      <td className="tcm-deduct__minus">
                        −{row.before - row.after}{" "}
                        <Trans i18nKey="tcmSystem.inventory.unit">{table.unit}</Trans>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="erp-flow__note">
            <i className="icofont-link"></i>
            <p>
              <Trans i18nKey="tcmSystem.inventory.note">{tcmInventory.note}</Trans>
            </p>
          </div>
        </div>
      </section>

      {/* Integration and Security Screens */}
      <section id="screens" className="section-padding erp-screens bg-one">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="tcmSystem.screens.subtitle">
                {tcmScreens.subtitle}
              </Trans>
            }
            title={
              <Trans i18nKey="tcmSystem.screens.title">{tcmScreens.title}</Trans>
            }
            direction="center"
          />
          <div className="erp-screens__tabs" role="tablist">
            {tcmScreens.screens.map((s) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={s.id === screen.id}
                className={`erp-screens__tab ${s.id === screen.id ? "is-active" : ""}`}
                onClick={() => setActiveScreen(s.id)}
              >
                <i className={s.icon}></i>
                <Trans i18nKey={`tcmSystem.screens.screen${s.id}.title`}>
                  {s.title}
                </Trans>
              </button>
            ))}
          </div>
          <div
            className={`erp-screens__frame${screen.narrow ? " tcm-frame--narrow" : ""}`}
            role="tabpanel"
          >
            <div className="erp-screens__frame--bar">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <a href={screenSrc} target="_blank" rel="noreferrer">
              <img key={screenSrc} className="img-fluid" src={screenSrc} alt={screen.title} />
            </a>
          </div>
          <div className="erp-screens__caption">
            <p>
              <Trans i18nKey={`tcmSystem.screens.screen${screen.id}.description`}>
                {screen.description}
              </Trans>
            </p>
            <span>
              <i className="icofont-expand"></i>{" "}
              <Trans i18nKey="tcmSystem.screens.enlarge">{tcmScreens.enlarge}</Trans>
            </span>
          </div>
        </div>
      </section>

      {/* Cloud or Local Server */}
      <section id="deploy" className="section-padding tcm-deploy">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="tcmSystem.deploy.subtitle">{tcmDeploy.subtitle}</Trans>
            }
            title={<Trans i18nKey="tcmSystem.deploy.title">{tcmDeploy.title}</Trans>}
            direction="center"
          />
          <div className="row pt-4 g-4 justify-content-center">
            {tcmDeploy.options.map((option) => (
              <div key={option.id} className="col-md-6 col-lg-5">
                <div
                  className={`tcm-deploy__card translateEffect1 h-100 tcm-deploy__card--${option.id}`}
                >
                  <div className="tcm-deploy__head">
                    <span className="tcm-deploy__icon">
                      <i className={option.icon}></i>
                    </span>
                    <h4>
                      <Trans i18nKey={`tcmSystem.deploy.option${option.id}.title`}>
                        {option.title}
                      </Trans>
                    </h4>
                  </div>
                  <ul>
                    {option.points.map((point, j) => (
                      <li key={j}>
                        <i className="icofont-check"></i>
                        <Trans
                          i18nKey={`tcmSystem.deploy.option${option.id}.point${j + 1}`}
                        >
                          {point}
                        </Trans>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          <p className="tcm-deploy__note">
            <Trans i18nKey="tcmSystem.deploy.note">{tcmDeploy.note}</Trans>
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing section-padding erp-pricing bg-one">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="tcmSystem.pricing.subtitle">{tcmPricing.subtitle}</Trans>
            }
            title={
              <Trans i18nKey="tcmSystem.pricing.title">{tcmPricing.title}</Trans>
            }
            direction="center"
          />

          <div className="erp-billing" role="group">
            <button
              type="button"
              className={`erp-billing__btn ${!isYearly ? "is-active" : ""}`}
              aria-pressed={!isYearly}
              onClick={() => setBilling("monthly")}
            >
              <Trans i18nKey="tcmSystem.pricing.monthly">{tcmPricing.monthly}</Trans>
            </button>
            <button
              type="button"
              className={`erp-billing__btn ${isYearly ? "is-active" : ""}`}
              aria-pressed={isYearly}
              onClick={() => setBilling("yearly")}
            >
              <Trans i18nKey="tcmSystem.pricing.yearly">{tcmPricing.yearly}</Trans>
            </button>
          </div>
          {isYearly && (
            <p className="erp-billing__save">
              <i className="icofont-tag"></i>{" "}
              <Trans i18nKey="tcmSystem.pricing.save">{tcmPricing.save}</Trans>
            </p>
          )}

          <div className="row justify-content-center pt-4">
            <div className="col-md-9 col-lg-6">
              <div className="pricing__item tcm-price translateEffect1">
                <span className="pricing__item__tag">
                  <Trans i18nKey="tcmSystem.pricing.plan">{tcmPricing.plan}</Trans>
                </span>
                <div className="pricing__item__icon">
                  <i className="icofont-herbal"></i>
                </div>
                <h3 className="fs-1 mt-2">
                  {tcmPricing.currency}
                  {isYearly ? tcmPricing.priceYearly : tcmPricing.priceMonthly}
                </h3>
                <p className="pb-3">
                  {isYearly ? (
                    <Trans i18nKey="tcmSystem.pricing.perYear">
                      {tcmPricing.perYear}
                    </Trans>
                  ) : (
                    <Trans i18nKey="tcmSystem.pricing.perMonth">
                      {tcmPricing.perMonth}
                    </Trans>
                  )}
                </p>
                <hr />
                <div className="pricing__item__list">
                  <ul className="pt-3">
                    {tcmPricing.features.map((feature, j) => (
                      <li key={j}>
                        <div className="pricing__item__list--ico">
                          <i className="icofont-check"></i>
                        </div>
                        <p>
                          <Trans i18nKey={`tcmSystem.pricing.feature${j + 1}`}>
                            {feature}
                          </Trans>
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="tcm-price__note">
                  <i className="icofont-info-circle"></i>
                  <p>
                    <Trans i18nKey="tcmSystem.pricing.integrationNote">
                      {tcmPricing.integrationNote}
                    </Trans>
                  </p>
                </div>
                <div className="tcm-price__contact">
                  <ContactButtons hero={tcmHero} i18nPrefix="tcmSystem.pricing" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Consultant Practitioners */}
      <section id="doctors" className="team3 section-padding tcm-doctors">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="tcmSystem.doctors.subtitle">{tcmDoctors.subtitle}</Trans>
            }
            title={
              <Trans i18nKey="tcmSystem.doctors.title">{tcmDoctors.title}</Trans>
            }
            direction="center"
          />
          <p className="tcm-doctors__intro">
            <Trans i18nKey="tcmSystem.doctors.description">
              {tcmDoctors.description}
            </Trans>
          </p>
          <div className="row justify-content-center gy-5">
            {tcmDoctors.doctors.map((doctor) => {
              const key = `tcmSystem.doctors.doctor${doctor.id}`;
              return (
                <div key={doctor.id} className="col-md-6 col-lg-5 d-flex">
                  <div className="team3__wrapper__card tcm-doctors__card w-100">
                    {doctor.image ? (
                      <img
                        className="img-fluid rounded-circle mx-auto"
                        src={doctor.image}
                        alt={doctor.name}
                      />
                    ) : (
                      <div className="tcm-doctors__avatar" aria-hidden="true">
                        <i className="icofont-doctor-alt"></i>
                      </div>
                    )}
                    <h4 className="mt-4 mb-1 fs-4">
                      <Trans i18nKey={`${key}.name`}>{doctor.name}</Trans>
                    </h4>
                    <p className="tcm-doctors__role">
                      <Trans i18nKey={`${key}.role`}>{doctor.role}</Trans>
                    </p>

                    <div className="tcm-doctors__details">
                      <h5>
                        <i className="icofont-graduate-alt"></i>{" "}
                        <Trans i18nKey="tcmSystem.doctors.qualificationsLabel">
                          {tcmDoctors.qualificationsLabel}
                        </Trans>
                      </h5>
                      <ul className="tcm-doctors__list">
                        {doctor.qualifications.map((item, j) => (
                          <li key={j}>
                            <Trans i18nKey={`${key}.qualification${j + 1}`}>{item}</Trans>
                          </li>
                        ))}
                      </ul>

                      <h5>
                        <i className="icofont-star"></i>{" "}
                        <Trans i18nKey="tcmSystem.doctors.specialtiesLabel">
                          {tcmDoctors.specialtiesLabel}
                        </Trans>
                      </h5>
                      <ul className="tcm-doctors__tags">
                        {doctor.specialties.map((item, j) => (
                          <li key={j}>
                            <Trans i18nKey={`${key}.specialty${j + 1}`}>{item}</Trans>
                          </li>
                        ))}
                      </ul>

                      <h5>
                        <i className="icofont-ui-user"></i>{" "}
                        <Trans i18nKey="tcmSystem.doctors.profileLabel">
                          {tcmDoctors.profileLabel}
                        </Trans>
                      </h5>
                      <ul className="tcm-doctors__list">
                        {doctor.profile.map((item, j) => (
                          <li key={j}>
                            <Trans i18nKey={`${key}.profile${j + 1}`}>{item}</Trans>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <Ctav3 className="tcm-cta" />

      <Footer2 />
    </>
  );
};

export default TcmSystem;
